import { Mic, Square } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { player } from '../audio/player';
import { micSupported, startRecording, type RecordingHandle, type RecordingResult } from '../audio/recorder';
import { markMicExplained, useAppState } from '../state/store';
import { Sheet } from './Sheet';

export type MicProblem = 'denied' | 'unsupported' | 'failed' | 'declined';

interface Props {
  label: string;
  onResult: (result: RecordingResult) => void;
  onProblem: (problem: MicProblem) => void;
  compact?: boolean;
  /** A small pill button, for recording again next to an earlier recording. */
  inline?: boolean;
}

type Phase = 'idle' | 'starting' | 'recording' | 'processing';

/**
 * The record button. Tap to record; it stops when you stop speaking, when you tap again,
 * or after 15 seconds. The first tap explains why the microphone is needed before the
 * browser asks.
 */
export function RecordControl({ label, onResult, onProblem, compact = false, inline = false }: Props) {
  const { profile, settings } = useAppState();
  const [phase, setPhase] = useState<Phase>('idle');
  const [explain, setExplain] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [missed, setMissed] = useState(false);
  const handle = useRef<RecordingHandle | null>(null);
  const levelRef = useRef<HTMLSpanElement>(null);
  const timer = useRef<number>(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearInterval(timer.current);
      handle.current?.cancel();
    };
  }, []);

  const begin = async () => {
    if (!micSupported()) {
      onProblem('unsupported');
      return;
    }
    player.stop();
    setPhase('starting');
    try {
      const h = await startRecording({
        autoStop: settings.autoStop,
        onLevel: (level) => levelRef.current?.style.setProperty('--level', level.toFixed(3)),
        onEnd: () => {
          clearInterval(timer.current);
          if (mounted.current) setPhase('processing');
        },
      });
      if (!mounted.current) {
        h.cancel();
        return;
      }
      handle.current = h;
      setSeconds(0);
      const started = Date.now();
      timer.current = window.setInterval(() => setSeconds(Math.floor((Date.now() - started) / 1000)), 250);
      setPhase('recording');
      const result = await h.result;
      handle.current = null;
      if (!mounted.current) {
        if (result) URL.revokeObjectURL(result.url);
        return;
      }
      setPhase('idle');
      setMissed(!result);
      if (result) onResult(result);
    } catch (err) {
      if (!mounted.current) return;
      setPhase('idle');
      const name = (err as DOMException)?.name;
      onProblem(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : name === 'NotFoundError' ? 'unsupported' : 'failed');
    }
  };

  const onClick = () => {
    player.prime();
    if (phase === 'recording') {
      handle.current?.stop();
      return;
    }
    if (phase !== 'idle') return;
    setMissed(false);
    if (!profile.micExplained) {
      setExplain(true);
      return;
    }
    begin();
  };

  const recording = phase === 'recording';
  const explainSheet = (
    <Sheet open={explain} onClose={() => setExplain(false)} title="Hear yourself next to the model">
      <p>
        Recording yourself lets you play your Greek right after the native-style model, which makes the difference easy to
        hear.
      </p>
      <p className="muted">Your recording stays on this device. It is never uploaded and is deleted when you move on.</p>
      <div className="stack">
        <button
          type="button"
          className="btn btn-primary btn-block"
          onClick={() => {
            setExplain(false);
            markMicExplained();
            begin();
          }}
        >
          <Mic size={20} aria-hidden="true" /> Use the microphone
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-block"
          onClick={() => {
            setExplain(false);
            markMicExplained();
            onProblem('declined');
          }}
        >
          Not now, I'll just say it aloud
        </button>
      </div>
    </Sheet>
  );

  if (inline) {
    return (
      <div className="record-inline">
        <button
          type="button"
          className={`btn record-again ${recording ? 'record-again-on' : 'btn-secondary'}`}
          onClick={onClick}
          aria-label={recording ? 'Stop recording' : undefined}
          disabled={phase === 'starting' || phase === 'processing'}
        >
          {recording ? <Square size={16} fill="currentColor" aria-hidden="true" /> : <Mic size={18} aria-hidden="true" />}
          {phase === 'idle' && label}
          {phase === 'starting' && 'Starting…'}
          {recording && `Stop · 0:${String(seconds).padStart(2, '0')}`}
          {phase === 'processing' && 'One moment…'}
        </button>
        {recording && (
          <span className="record-inline-level" aria-hidden="true">
            <span ref={levelRef} />
          </span>
        )}
        <span className="small muted" aria-live="polite">
          {recording ? 'Speak now' : missed ? "Didn't catch anything. Try again." : ''}
        </span>
        {explainSheet}
      </div>
    );
  }

  return (
    <div className="record">
      <button
        type="button"
        className="record-btn"
        data-state={recording ? 'recording' : 'idle'}
        onClick={onClick}
        aria-label={recording ? 'Stop recording' : label}
        disabled={phase === 'starting' || phase === 'processing'}
        style={compact ? { width: 68, height: 68 } : undefined}
      >
        {recording && <span ref={levelRef} className="record-level" aria-hidden="true" />}
        {recording ? <Square size={26} fill="currentColor" aria-hidden="true" /> : <Mic size={compact ? 28 : 34} aria-hidden="true" />}
      </button>
      <div className="record-label" aria-live="polite">
        {phase === 'idle' && (missed ? "Didn't catch anything. Tap, then speak." : label)}
        {phase === 'starting' && 'Starting the microphone…'}
        {recording && (
          <>
            <span className="record-timer">0:{String(seconds).padStart(2, '0')}</span> · Speak now, tap to stop
          </>
        )}
        {phase === 'processing' && 'One moment…'}
      </div>
      {explainSheet}
    </div>
  );
}
